import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./CreateMessage.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
  CustomTextInput,
} from "../../../components";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchClassOptions,
  fetchSectionOptions,
  fetchStaffClassAndSubjects,
} from "../../../redux/Masterdata";
import { useNavigate } from "react-router-dom";
import PopupModal from "../../../components/PopupModal/PopupModal";

const CreateMessage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const { translations } = useSelector((state) => state.language);
  const clientData = user?.client_data[0] || {};
  const userId = user?.id || "";
  const roleId = Number(user?.role?.id);
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const master_id = clientData?.master_id || "";

  // Redux state for class & section
  const {
    classOptions: reduxClassOptions,
    staffClassAndSubjectOptions,
    sectionOptions: reduxSectionOptions,
  } = useSelector((state) => state.masterData);
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  const [formData, setFormData] = useState({
    school: null,
    role: null,
    staffType: null,
    class: null,
    section: null,
    student: null,
    teacher: null,
    subject: "",
    message: "",
    original_name: null,
    upload_path: "",
  });
  const [errors, setErrors] = useState({});
  const [schoolOptions, setSchoolOptions] = useState([
    { value: "0", label: translations.labels?.allSchool || "All School" },
  ]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);

  // Safe getters for conditional rendering
  const selectedRoleValue = Number(formData.role?.value ?? -1);
  const selectedStaffTypeValue = Number(formData.staffType?.value ?? -1);

  // ==================== VISIBILITY HELPERS ====================
  const showClassFields =
    roleId !== 2 &&
    formData.role &&
    selectedRoleValue !== 0 &&
    ((selectedRoleValue === 1 &&
      formData.staffType &&
      selectedStaffTypeValue === 1) ||
      selectedRoleValue === 2 ||
      selectedRoleValue === 3);

  const showStudentField =
    showClassFields && (selectedRoleValue === 2 || selectedRoleValue === 3);

  const showTeacherField = showClassFields && selectedRoleValue === 1;

  // ==================== HELPERS ====================
  const isAllSelected = (field) => {
    if (!field) return false;
    if (Array.isArray(field)) return field.some((item) => item.value === "All");
    return field?.value === "All";
  };

  // Get class IDs - if "All" selected, returns ALL real IDs from dropdown options
  const getSelectedClassIds = (classDropdownOpts) => {
    if (!formData.class || !Array.isArray(formData.class)) return [];
    if (isAllSelected(formData.class)) {
      // "All Class" → collect all real class IDs from options
      return (classDropdownOpts || [])
        .filter((item) => item.value !== "All")
        .map((item) => parseInt(item.value));
    }
    return formData.class
      .filter((item) => item.value !== "All")
      .map((item) => parseInt(item.value));
  };

  // Get section values - if "All" selected, returns ALL real values from dropdown options
  const getSelectedSectionValues = (sectionDropdownOpts) => {
    if (!formData.section || !Array.isArray(formData.section)) return [];
    if (isAllSelected(formData.section)) {
      // "All Section" → collect all real section values from options
      return (sectionDropdownOpts || [])
        .filter((item) => item.value !== "All")
        .map((item) => item.value);
    }
    return formData.section
      .filter((item) => item.value !== "All")
      .map((item) => item.value);
  };

  // Get student IDs - if "All" selected, returns ALL real IDs from studentOptions
  const getSelectedStudentIds = () => {
    if (!formData.student || !Array.isArray(formData.student)) return [];
    if (isAllSelected(formData.student)) {
      // "All Student" → collect all real student IDs
      return studentOptions.map((item) => parseInt(item.value));
    }
    return formData.student
      .filter((item) => item.value !== "All")
      .map((item) => parseInt(item.value));
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (roleId === 2) {
      fetchDashboardData();
    }
  }, []);

  useEffect(() => {
    if (roleId === 5) {
      if (school_id && academic_year) {
        dispatch(
          fetchClassOptions({
            id: parseInt(school_id),
            academic_year: academic_year,
          }),
        );
      }
    } else if (roleId === 3) {
      if (master_id) {
        dispatch(
          fetchStaffClassAndSubjects({
            staff_id: parseInt(master_id),
            class_id: "",
            section_id: "",
            subject_id: "",
          }),
        );
      }
    }
  }, [roleId, school_id, academic_year, master_id, dispatch]);

  // Fetch section options when class is selected
  // "All Class" → sends all real class IDs as array
  useEffect(() => {
    if (formData.class && roleId === 5) {
      const classIds = getSelectedClassIds(classDropdownOptions);
      if (classIds.length > 0) {
        dispatch(
          fetchSectionOptions({
            id: classIds, // [58, 59, 60] even when "All" selected
            school_id: parseInt(school_id),
          }),
        );
      }
    }
  }, [formData.class, roleId, school_id, dispatch]);

  // Fetch student & teacher when class AND section are selected
  // "All" → sends all real IDs/values as arrays
  useEffect(() => {
    if (formData.class && formData.section) {
      const classIds = getSelectedClassIds(classDropdownOptions);
      const sectionValues = getSelectedSectionValues(sectionDropdownOptions);

      if (classIds.length > 0 && sectionValues.length > 0) {
        fetchStudentList(classIds, sectionValues);
        fetchTeacherByClassSection(classIds, sectionValues);
      }
    } else {
      setStudentOptions([]);
      setTeacherOptions([]);
      setFormData((prev) => ({
        ...prev,
        student: null,
        teacher: null,
      }));
    }
  }, [formData.class, formData.section]);

  // ==================== API CALLS ====================
  const fetchDashboardData = async () => {
    try {
      const response = await AxiosInstance.get(
        `/groupCockpit/Dashboard/${userId}`,
      );

      if (response.data?.success && Array.isArray(response.data.data)) {
        const dashboardData = response.data.data;
        const schools =
          dashboardData[0]?.schools && Array.isArray(dashboardData[0].schools)
            ? dashboardData[0].schools
            : [];

        const formattedSchools = schools.map((school) => ({
          value: school.id,
          label: school.short_school_name,
        }));

        setSchoolOptions([
          {
            value: "0",
            label: translations.labels?.allSchool || "All School",
          },
          ...formattedSchools,
        ]);
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch dashboard data",
        );
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
  };

  const fetchStudentList = async (classIds, sectionValues) => {
    try {
      const payload = {
        class_id: classIds,
        section_id: sectionValues,
        school_id: parseInt(school_id),
      };
      const response = await AxiosInstance.post(
        "/message/studentList",
        payload,
      );

      if (response.data?.status && Array.isArray(response.data.students)) {
        const options = response.data.students.map((student) => ({
          value: student.id,
          label: student.student_name,
        }));
        setStudentOptions(options);
      } else {
        setStudentOptions([]);
      }
    } catch (error) {
      console.error("Error fetching student list:", error);
      setStudentOptions([]);
    }
  };

  const fetchTeacherByClassSection = async (classIds, sectionValues) => {
    try {
      const payload = {
        class_id: classIds,
        section_id: sectionValues,
        school_id: parseInt(school_id),
      };
      const response = await AxiosInstance.post("/message/getStaff", payload);

      if (response.data?.status && response.data.class_teacher) {
        const teacher = response.data.class_teacher;
        setTeacherOptions([
          {
            value: teacher.id,
            label: teacher.class_teacher,
          },
        ]);
        setFormData((prev) => ({
          ...prev,
          teacher: {
            value: teacher.id,
            label: teacher.class_teacher,
          },
        }));
      } else {
        setTeacherOptions([]);
      }
    } catch (error) {
      console.error("Error fetching teacher:", error);
      setTeacherOptions([]);
    }
  };

  // ==================== DROPDOWN OPTIONS ====================
  const classDropdownOptions = useMemo(() => {
    const sourceData =
      roleId === 3 ? staffClassAndSubjectOptions : reduxClassOptions;

    const options = sourceData.map((item) => ({
      value: item.class_id ?? item.value,
      label: item.class_name ?? item.label,
      classId: item.class_id ?? item.value,
      className: item.class_name ?? item.label,
      sectionId: item.section_id || "",
      subjectId: item.subject_id || "",
      subjectName: item.subject_name || "",
    }));

    const uniqueOptions = options.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.value === item.value),
    );

    if (uniqueOptions.length > 1) {
      return [
        {
          value: "All",
          label: translations.labels?.allClass || "All Class",
        },
        ...uniqueOptions,
      ];
    }

    return uniqueOptions;
  }, [reduxClassOptions, staffClassAndSubjectOptions, roleId, translations]);

  const sectionDropdownOptions = useMemo(() => {
    let options = [];

    if (roleId === 3 && formData.class) {
      const selectedClasses = Array.isArray(formData.class)
        ? formData.class
        : [formData.class];

      const allSelected = selectedClasses.some((item) => item.value === "All");

      if (allSelected) {
        const filtered = staffClassAndSubjectOptions
          .filter((item) => item.section_id)
          .map((item) => ({
            value: item.section_id,
            label: item.section_id,
          }));

        options = filtered.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.value === item.value),
        );
      } else {
        const selectedClassIds = selectedClasses.map((item) =>
          String(item.value),
        );

        const filtered = staffClassAndSubjectOptions
          .filter(
            (item) =>
              selectedClassIds.includes(String(item.class_id)) &&
              item.section_id,
          )
          .map((item) => ({
            value: item.section_id,
            label: item.section_id,
          }));

        options = filtered.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.value === item.value),
        );
      }
    } else {
      options = reduxSectionOptions.map((item) => ({
        value: item.value,
        label: item.label,
      }));
    }

    if (options.length > 1) {
      return [
        {
          value: "All",
          label: translations.labels?.allSection || "All Section",
        },
        ...options,
      ];
    }

    return options;
  }, [
    reduxSectionOptions,
    staffClassAndSubjectOptions,
    roleId,
    formData.class,
    translations,
  ]);

  const studentDropdownOptions = useMemo(() => {
    if (studentOptions.length === 0) return [];

    if (studentOptions.length > 1) {
      return [
        {
          value: "All",
          label: translations.labels?.allStudents || "All Students",
        },
        ...studentOptions,
      ];
    }

    return studentOptions;
  }, [studentOptions, translations]);

  const roleOptions = useMemo(() => {
    switch (roleId) {
      case 5:
        return [
          {
            value: "0",
            label: translations.labels?.allMembers || "All Members",
          },
          {
            value: "1",
            label: translations.labels?.staffAccount || "Staff Account",
          },
          {
            value: "2",
            label: translations.labels?.studentAccount || "Student Account",
          },
          {
            value: "3",
            label: translations.labels?.parentAccount || "Parent Account",
          },
        ];
      case 3:
        return [
          {
            value: "2",
            label: translations.labels?.studentAccount || "Student Account",
          },
          {
            value: "3",
            label: translations.labels?.parentAccount || "Parent Account",
          },
        ];
      case 2:
        return [
          {
            value: "0",
            label: translations.labels?.allMembers || "All Members",
          },
          {
            value: "4",
            label: translations.labels?.schoolCockpit || "School Cockpit",
          },
        ];
      default:
        return [];
    }
  }, [roleId, translations]);

  const staffTypeOptions = useMemo(
    () => [
      { value: "0", label: translations.labels?.allStaff || "All Staff" },
      { value: "1", label: translations.labels?.teaching || "Teaching" },
      {
        value: "2",
        label: translations.labels?.administrative || "Administrative",
      },
      { value: "3", label: translations.labels?.management || "Management" },
    ],
    [translations],
  );

  // ==================== HANDLERS ====================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectAll = (newValue, prevValue, allOptions) => {
    if (!Array.isArray(newValue)) return newValue;

    const allOptionItems = allOptions.filter((opt) => opt.value !== "All");
    const prevHadAll = Array.isArray(prevValue)
      ? prevValue.some((item) => item.value === "All")
      : false;
    const nowHasAll = newValue.some((item) => item.value === "All");

    if (nowHasAll && !prevHadAll) return allOptions;
    if (!nowHasAll && prevHadAll) return [];
    if (prevHadAll && nowHasAll)
      return newValue.filter((item) => item.value !== "All");
    if (!nowHasAll && newValue.length === allOptionItems.length)
      return allOptions;

    return newValue;
  };

  const handleDropdownChange = (name, value) => {
    if (name === "role") {
      setFormData((prev) => ({
        ...prev,
        role: value,
        staffType: null,
        class: null,
        section: null,
        student: null,
        teacher: null,
      }));
      setStudentOptions([]);
      setTeacherOptions([]);
    } else if (name === "staffType") {
      setFormData((prev) => ({
        ...prev,
        staffType: value,
        class: null,
        section: null,
        student: null,
        teacher: null,
      }));
      setStudentOptions([]);
      setTeacherOptions([]);
    } else if (name === "class") {
      const newValue = handleSelectAll(
        value,
        formData.class,
        classDropdownOptions,
      );
      setFormData((prev) => ({
        ...prev,
        class: newValue,
        section: null,
        student: null,
        teacher: null,
      }));
      setStudentOptions([]);
      setTeacherOptions([]);
    } else if (name === "section") {
      const newValue = handleSelectAll(
        value,
        formData.section,
        sectionDropdownOptions,
      );
      setFormData((prev) => ({
        ...prev,
        section: newValue,
        student: null,
        teacher: null,
      }));
    } else if (name === "student") {
      const newValue = handleSelectAll(
        value,
        formData.student,
        studentDropdownOptions,
      );
      setFormData((prev) => ({ ...prev, student: newValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = useCallback(async (file) => {
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        original_name: null,
        upload_path: "",
      }));
      setErrors((prev) => ({ ...prev, attachment: "" }));
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        attachment:
          translations.labels?.onlyImageFilesAllowed ||
          "Only jpg, jpeg, png, gif files are allowed",
      }));
      return;
    }

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("content_image", file);
      formDataPayload.append("type", "Communications");
      formDataPayload.append("school_id", school_id);

      const response = await AxiosInstance.post(
        "/content/upload",
        formDataPayload,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        const uploadedFile = response.data[0];
        setFormData((prev) => ({
          ...prev,
          original_name: uploadedFile.original_name,
          upload_path: uploadedFile.upload_path,
        }));
        setErrors((prev) => ({ ...prev, attachment: "" }));
      } else {
        throw new Error("Invalid upload response");
      }
    } catch (error) {
      console.error("File upload error:", error);
      setErrors((prev) => ({
        ...prev,
        attachment:
          translations.labels?.fileUploadFailed ||
          "Failed to upload file. Please try again.",
      }));
    }
  }, [school_id]);

  // ==================== VALIDATION ====================
  const validateForm = () => {
    const newErrors = {};

    if (roleId === 2 && !formData.school) {
      newErrors.school =
        translations.labels?.schoolRequired || "School is required";
    }

    if (!formData.role) {
      newErrors.role = translations.labels?.roleRequired || "Role is required";
    }

    if (
      roleId !== 2 &&
      roleId !== 3 &&
      formData.role &&
      selectedRoleValue !== 0
    ) {
      if (selectedRoleValue === 1 && !formData.staffType) {
        newErrors.staffType =
          translations.labels?.staffTypeRequired || "Staff Type is required";
      }

      if (showClassFields) {
        if (
          !formData.class ||
          (Array.isArray(formData.class) && formData.class.length === 0)
        ) {
          newErrors.class =
            translations.labels?.classRequired || "Class is required";
        }
        if (
          Array.isArray(formData.class) &&
          formData.class.length <= 1 &&
          (!formData.section ||
            (Array.isArray(formData.section) && formData.section.length === 0))
        ) {
          newErrors.section =
            translations.labels?.sectionRequired || "Section is required";
        }
      }

      // Student is required ONLY when exactly 1 class + 1 section selected (not "All")
      const isStudentVisible =
        showStudentField &&
        Array.isArray(formData.class) &&
        formData.class.length === 1 &&
        !formData.class.some((item) => item.value === "All") &&
        Array.isArray(formData.section) &&
        formData.section.length === 1 &&
        !formData.section.some((item) => item.value === "All");

      if (
        isStudentVisible &&
        (!formData.student ||
          (Array.isArray(formData.student) && formData.student.length === 0))
      ) {
        newErrors.student =
          translations.labels?.studentRequired || "Student is required";
      }

      if (
        showTeacherField &&
        (!Array.isArray(formData.class) || formData.class.length <= 1) &&
        (!Array.isArray(formData.section) || formData.section.length <= 1) &&
        !formData.teacher
      ) {
        newErrors.teacher =
          translations.labels?.teacherRequired || "Teacher is required";
      }
    }

    if (!formData.subject.trim()) {
      newErrors.subject =
        translations.labels?.subjectRequired || "Subject is required";
    }
    if (!formData.message.trim()) {
      newErrors.message =
        translations.labels?.messageRequired || "Message is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== SUBMIT ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // class: all real IDs as array (even for "All")
      const classArray = (() => {
        if (
          !formData.class ||
          (Array.isArray(formData.class) && formData.class.length === 0)
        )
          return [0];
        return getSelectedClassIds(classDropdownOptions);
      })();

      // section: all real values as array (even for "All")
      const sectionArray = (() => {
        if (
          !formData.section ||
          (Array.isArray(formData.section) && formData.section.length === 0)
        )
          return ["0"];
        return getSelectedSectionValues(sectionDropdownOptions);
      })();

      // student_id: all real IDs as array (even for "All")
      const studentIdArray = (() => {
        if (
          !formData.student ||
          (Array.isArray(formData.student) && formData.student.length === 0)
        )
          return [0];
        return getSelectedStudentIds();
      })();

      // student_name: 0 if All/empty, else first selected name
      const studentName = (() => {
        if (
          !formData.student ||
          (Array.isArray(formData.student) && formData.student.length === 0) ||
          isAllSelected(formData.student)
        )
          return 0;
        return Array.isArray(formData.student)
          ? formData.student[0]?.label || 0
          : formData.student?.label || 0;
      })();

      // Teacher info
      const teacherId = formData.teacher
        ? parseInt(formData.teacher?.value) || 0
        : 0;
      const teacherName = formData.teacher ? formData.teacher?.label || "" : "";
      const hasTeacher = formData.teacher ? 1 : 0;

      const hasSpecificStudent =
        formData.student &&
        Array.isArray(formData.student) &&
        formData.student.length > 0 &&
        !isAllSelected(formData.student);

      const payload = {
        created_by: parseInt(userId),
        client_id: clientData?.client_id || "",
        school_id:
          roleId === 2
            ? parseInt(formData.school?.value || 0)
            : parseInt(school_id || 0),
        role: roleId === 2 ? 4 : parseInt(formData?.role?.value) || 0,
        staff: parseInt(formData?.staffType?.value) || 0,
        class: classArray,
        section: classArray.length > 1 ? [] : sectionArray,
        school_user: parseInt(formData?.role?.value === "4" ? 1 : 0) || 0,
        is_teacher: hasTeacher,
        is_student: hasSpecificStudent ? 1 : 0,
        teacher_name:
          classArray.length > 1 || sectionArray.length > 1 ? "" : teacherName,
        teacher_id:
          classArray.length > 1 || sectionArray.length > 1 ? 0 : teacherId,
        student_id:
          classArray.length > 1 || sectionArray.length > 1 ? 0 : studentIdArray,
        student_name:
          classArray.length > 1 || sectionArray.length > 1 ? 0 : studentName,
        subject: formData?.subject,
        message: formData?.message,
        message_original_path: formData?.original_name || null,
        message_path: formData?.upload_path || null,
      };

      const response = await AxiosInstance.post(`/message/create`, payload);

      if (response.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message || translations.labels?.messageSentSuccess,
          redirectPath: "/user/messaging/email",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message,
          redirectPath: "",
        });
        throw new Error(response.data?.message || "Message send failed");
      }
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const handleCancel = () => {
    setFormData({
      school: null,
      role: null,
      staffType: null,
      class: null,
      section: null,
      student: null,
      teacher: null,
      subject: "",
      message: "",
      original_name: null,
      upload_path: "",
    });
    setErrors({});
    setStudentOptions([]);
    setTeacherOptions([]);
    navigate("/user/dashboard");
  };
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;

    setModalState((prev) => ({
      ...prev,
      open: false,
    }));

    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  // ==================== RENDER ====================
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <i className="fa-solid fa-table-cells"></i>
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.messaging || "Messaging"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* School - Group Cockpit only */}
            {roleId === 2 && (
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomDropdown
                  label={translations.labels?.school || "School"}
                  name="school"
                  value={formData.school}
                  onChange={(value) => handleDropdownChange("school", value)}
                  options={schoolOptions}
                  placeholder={
                    translations.labels?.selectSchoolPlaceholder ||
                    "Select School"
                  }
                  required
                  error={errors.school}
                  isClearable={true}
                  isSearchable={true}
                />
              </div>
            )}

            {/* Role */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.role || "Role"}
                name="role"
                value={formData.role}
                onChange={(value) => handleDropdownChange("role", value)}
                options={roleOptions}
                placeholder={translations.labels?.select || "Select"}
                required
                error={errors.role}
                isClearable={true}
                isSearchable={true}
              />
            </div>

            {/* Conditional fields */}
            {roleId !== 2 && formData.role && selectedRoleValue !== 0 && (
              <>
                {/* Staff Type - only for Staff Account (1) */}
                {selectedRoleValue === 1 && (
                  <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                    <CustomDropdown
                      label={translations.labels?.staffType || "Staff Type"}
                      name="staffType"
                      value={formData.staffType}
                      onChange={(value) =>
                        handleDropdownChange("staffType", value)
                      }
                      options={staffTypeOptions}
                      placeholder={translations.labels?.select || "Select"}
                      required
                      error={errors.staffType}
                      isClearable={true}
                      isSearchable={true}
                    />
                  </div>
                )}

                {/* Class, Section, Student, Teacher */}
                {showClassFields && (
                  <>
                    <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                      <CustomDropdown
                        label={translations.labels?.class || "Class"}
                        name="class"
                        value={formData.class}
                        onChange={(value) =>
                          handleDropdownChange("class", value)
                        }
                        options={classDropdownOptions}
                        placeholder={translations.labels?.select || "Select"}
                        required
                        error={errors.class}
                        isClearable={true}
                        isSearchable={true}
                        isMulti={true}
                        closeMenuOnSelect={false}
                      />
                    </div>
                    {Array.isArray(formData.class) &&
                      formData.class.length === 1 &&
                      !formData.class.some((item) => item.value === "All") && (
                        <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                          <CustomDropdown
                            label={translations.labels?.section || "Section"}
                            name="section"
                            value={formData.section}
                            onChange={(value) =>
                              handleDropdownChange("section", value)
                            }
                            options={sectionDropdownOptions}
                            placeholder={
                              translations.labels?.select || "Select"
                            }
                            required
                            error={errors.section}
                            isClearable={true}
                            isSearchable={true}
                            isMulti={true}
                            closeMenuOnSelect={false}
                          />
                        </div>
                      )}

                    {/* Student - role 2 (Student) or 3 (Parent) */}
                    {showStudentField &&
                      Array.isArray(formData.class) &&
                      formData.class.length === 1 &&
                      !formData.class.some((item) => item.value === "All") &&
                      Array.isArray(formData.section) &&
                      formData.section.length === 1 &&
                      !formData.section.some(
                        (item) => item.value === "All",
                      ) && (
                        <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                          <CustomDropdown
                            label={translations.labels?.student || "Student"}
                            name="student"
                            value={formData.student}
                            onChange={(value) =>
                              handleDropdownChange("student", value)
                            }
                            options={studentDropdownOptions}
                            placeholder={
                              translations.labels?.select || "Select"
                            }
                            required
                            error={errors.student}
                            isClearable={true}
                            isSearchable={true}
                            isMulti={true}
                            closeMenuOnSelect={false}
                          />
                        </div>
                      )}

                    {/* Teacher - role 1 (Staff) + Teaching */}
                    {showTeacherField &&
                      Array.isArray(formData.class) &&
                      formData.class.length === 1 &&
                      !formData.class.some((item) => item.value === "All") &&
                      Array.isArray(formData.section) &&
                      formData.section.length === 1 &&
                      !formData.section.some(
                        (item) => item.value === "All",
                      ) && (
                        <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                          <CustomDropdown
                            label={translations.labels?.teacher || "Teacher"}
                            name="teacher"
                            value={formData.teacher}
                            onChange={(value) =>
                              handleDropdownChange("teacher", value)
                            }
                            options={teacherOptions}
                            placeholder={
                              translations.labels?.select || "Select"
                            }
                            required
                            error={errors.teacher}
                            isClearable={true}
                            isSearchable={true}
                          />
                        </div>
                      )}
                  </>
                )}
              </>
            )}

            {/* Subject */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.subject || "Subject"}
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.subjectPlaceholder || "Enter Subject"
                }
                required
                error={errors.subject}
              />
            </div>

            {/* Message */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.message || "Message"}
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder={
                  translations.labels?.messagePlaceholder || "Enter Message"
                }
                required
                error={errors.message}
              />
            </div>

            {/* Attachment */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomFileInput
                label={translations.labels?.attachment || "Attachment"}
                name="attachment"
                value={formData.original_name || null}
                onChange={handleFileChange}
                accept="image/*"
                placeholder={
                  formData.original_name
                    ? formData.original_name
                    : translations.labels?.noFileChosen || "No file chosen"
                }
                buttonText={translations.labels?.chooseFile || "Choose File"}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.buttonGroup}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.sendButton}
              fullWidth={false}
              icon={<i className="fa-solid fa-paper-plane"></i>}
              iconPosition="left"
            >
              {translations.labels?.send || "Send"}
            </CustomButton>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
              icon={<i className="fa-solid fa-circle-xmark"></i>}
              iconPosition="left"
            >
              {translations.labels?.cancel || "Cancel"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateMessage;